import React from 'react';
import type { OffersComparisonCard as OffersComparisonCardType } from '../../types/structuredComponents';

interface OffersComparisonCardProps {
  data?: OffersComparisonCardType;
  // Alternative props structure when passed through getComponentProps
  title?: string;
  description?: string;
  offers?: Array<{
    name: string;
    price: string;
    features: string[];
  }>;
  ctaText?: string;
  onCtaClick?: (offer: unknown) => void;
  isCarousel?: boolean;
}

const OffersComparisonCard: React.FC<OffersComparisonCardProps> = ({ 
  data, 
  title, 
  description, 
  offers,
  isCarousel
}) => {
  // Debug logging to see all props
  console.log('OffersComparisonCard received props:', {
    data,
    title,
    description,
    offers
  });
  console.log('Data type:', typeof data);
  console.log('Offers type:', typeof offers);
  console.log('Offers length:', offers?.length);
  
  // Provider icon mapping
  const getProviderIcon = (bundleName: string): string => {
    const name = bundleName.toLowerCase();
    if (name.includes('super streamer') || name.includes('superstreamer')) return '🚀';
    if (name.includes('mobile') || name.includes('phone')) return '📱';
    if (name.includes('broadband') || name.includes('internet')) return '💻';
    if (name.includes('streaming') || name.includes('netflix')) return '📺';
    if (name.includes('gaming') || name.includes('game')) return '🎮';
    if (name.includes('premium') || name.includes('pro')) return '⭐';
    if (name.includes('basic') || name.includes('starter')) return '📊';
    if (name.includes('unlimited') || name.includes('max')) return '♾️';
    if (name.includes('fiber') || name.includes('fibre')) return '⚡';
    if (name.includes('5g')) return '📡';
    return '📋'; // Default icon
  };


  // Determine which data structure we're working with
  const isOriginalDataStructure = data && data.content;
  
  // Handle undefined or invalid data for original structure
  if (isOriginalDataStructure) {
    if (!data || !data.content || !Array.isArray(data.content) || data.content.length === 0) {
      return (
        <div className="offers-comparison-error">
          No comparison data available
        </div>
      );
    }
  } else {
    // Handle transformed props structure
    if (!offers || !Array.isArray(offers) || offers.length === 0) {
      return (
        <div className="offers-comparison-error">
          No comparison data available
        </div>
      );
    }
  }

  // Render using original data structure
  if (isOriginalDataStructure && data?.content) {
    return (
      <div className="offers-comparison" style={{ height: isCarousel ? '100%' : 'auto' }}>
        {data.content.map((item, index) => (
          <div key={index} className="offers-comparison__container">
            {item.header && (
              <div className="offers-comparison__category">
                {item.header || "COMPARISON"}
              </div>
            )}
            
            {item.title && (
              <h3 className="offers-comparison__title">
                {item.title}
              </h3>
            )}
            
            {item.description && (
              <p className="offers-comparison__description">
                {item.description}
              </p>
            )}
            
            {item.options && Array.isArray(item.options) && (
              <div className="offers-comparison__options">
                {item.options.map((option, optionIndex) => (
                  <div 
                    key={optionIndex} 
                    className="offers-comparison__option"
                  >
                    <div className="offers-comparison__option-content">
                      <span className="offers-comparison__option-icon">
                        {getProviderIcon(option.bundleName)}
                      </span>
                      <span className="offers-comparison__option-name">
                        {option.bundleName}
                      </span>
                      <span className="offers-comparison__option-price">
                        {option.amount}
                      </span>
                    </div>
                    {optionIndex == 0 &&  <div className="offers-comparison__option-separator"></div> }
                   
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <div className="offers-comparison__cta-container">
          <a href="https://switcher.ie/" className="offers-comparison__cta" target="_blank" rel="noopener noreferrer">
            Compare Offers
          </a> 
        </div>
      </div>
    );
  }

  

  // Render using transformed props structure
  return (
    <div className="offers-comparison" style={{ height: isCarousel ? '100%' : 'auto' }}>
      <div className="offers-comparison__container"> 
        {title && (
          <h3 className="offers-comparison__title">
            {title}
          </h3>
        )}
        
        {description && (
          <p className="offers-comparison__description">
            {description}
          </p>
        )}
        
        {offers && Array.isArray(offers) && (
          <div className="offers-comparison__options">
            {offers.map((offer, optionIndex) => (
              <div 
                key={optionIndex} 
                className="offers-comparison__option"
              >
                <div className="offers-comparison__option-content">
                  <span className="offers-comparison__option-name">
                    {offer.name}
                  </span>
                  <span className="offers-comparison__option-price">
                    {offer.price}/m
                  </span>
                </div>
                 {optionIndex == 0 &&  <div className="offers-comparison__option-separator"></div> }
                   
              </div>
            ))}
          </div>
        )}
        
        <div className="offers-comparison__cta-container">
          <a href="https://switcher.ie/" className="offers-comparison__cta" target="_blank" rel="noopener noreferrer">
            Compare Offers
          </a> 
        </div>

        <div className="offers-comparison__disclaimer">
          <span style={{ fontWeight: 'bold' }}>Disclaimer:</span> Offer comparisons are based on publicly available data from Switcher.ie. Pricing and features may vary by provider and location, and we are not liable for any subsequent changes.
        </div>
      
      </div>
    </div>
  );
};

export default OffersComparisonCard;
